import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { Column, ExpandedColumn } from '@repo/ui/data-table'
import { FileContractIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useState } from 'react'

import { API, constructApiUrl } from '../../api/apis.config'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import { QualificationBadge } from '../../components/elements/qualification-badge'
import { ContractFilter } from '../../components/pages/contracts/contract-filter'
import useAuth from '../../lib/hooks/use-auth'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { staffPositionEnumMapping } from '../../lib/types/enums/staff/staff-position.enum'
import { ContractFilters, ContractLight } from '../../lib/types/models/contract/contract'
import { ContractProperties } from '../../lib/types/models/contract/contract.properties'
import { MissionToStaffCompact } from '../../lib/types/models/mission-to-staff/mission-to-staff'
import {
  transformContractFiltersToUrl,
  transformUrlToContractFilters
} from '../../lib/utils/filters-mapping/contract'
import { viewFileByPdfJs } from '../../utils/functions/view-file-by-google-docs'

export function StaffContractsPage() {
  const { t } = useTranslate()
  const { user } = useAuth()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { modalState, toggleModal, closeModel } = useModalManager<'filters', ContractFilters>([
    'filters'
  ])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<ContractFilters>({
      initialFilters: {},
      options: { allowSearchParam: true },
      transformFilterToUrl: transformContractFiltersToUrl,
      transformUrlToFilter: transformUrlToContractFilters
    })

  const columns: Column<ContractLight>[] = [
    {
      accessorKey: 'mission-dates',
      header: t('staff.personal.space.contracts.dates'),
      cell: ({ row }) => {
        const dateRange = ContractProperties.getDateRange(row.original)

        if (!dateRange) return '-'
        return (
          <Badge
            text={`${DateFormatter.DDMMYYYY(dateRange.startDate)} - ${DateFormatter.DDMMYYYY(dateRange.endDate)}`}
          />
        )
      }
    },
    {
      accessorKey: 'mission-title',
      header: t('staff.personal.space.contracts.country'),
      cell: ({ row }) => {
        const countryCode = ContractProperties.getCountryCode(row.original)
        if (!countryCode) return '-'
        return <CountryFlag code={countryCode} />
      }
    },
    {
      accessorKey: 'mission-pay',
      header: t('staff.personal.space.contracts.position'),
      cell: ({ row }) => {
        const position = ContractProperties.getStaffPosition(row.original)
        return (
          <Typography variant="secondary-body">
            {valueRender(position, (p) => t(staffPositionEnumMapping[p]))}
          </Typography>
        )
      }
    },
    {
      accessorKey: 'contractSentAt',
      header: t('staff.personal.space.contracts.qualification'),
      cell: ({ row }) => {
        const qualification = ContractProperties.getStaffQualification(row.original)
        if (!qualification) return '-'
        return <QualificationBadge qualification={qualification} />
      }
    },
    {
      accessorKey: 'contractSignedAt',
      header: t('staff.personal.space.contracts.net.remuneration.before.PASE'),
      cell: ({ row }) => {
        const netRemunerationBeforePASE = ContractProperties.getNetRemunerationBeforePASE(
          row.original
        )
        if (!netRemunerationBeforePASE) return '-'
        return (
          <Badge
            text={NumberFormatter.formatCurrency(
              netRemunerationBeforePASE,
              ContractProperties.getCurrency(row.original)
            )}
            variant="secondaryBlue"
          />
        )
      }
    },
    {
      accessorKey: 'documentFile',
      header: t('staff.personal.space.contracts.status'),
      cell: ({ row }) => {
        const { contractSignedAt } = row.original
        if (contractSignedAt) return <Badge text={'Signé'} variant={'success'} />
        return (
          <Badge
            text={'En attente'}
            variant={'warning'}
            color={tailwindColors.orange.light.DEFAULT}
            textColor={tailwindColors.orange.DEFAULT}
          />
        )
      }
    },
    {
      accessorKey: '',
      header: t('staff.personal.space.contracts.date.of.signature'),
      enableSorting: true,
      cell: ({ row }) => {
        const { contractSignedAt } = row.original
        if (!contractSignedAt) return '-'
        return <Badge text={DateFormatter.DDMMYYYY(contractSignedAt)} variant="grey" />
      }
    },
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => {
        const documentFile = row.original?.documentFile

        if (!documentFile) return '-'
        return (
          <Typography.Link
            variant={'label-medium'}
            color={tailwindColors.navyBlue.DEFAULT}
            className="mr-5"
            onClick={(e) => {
              e.stopPropagation()
              if (documentFile) viewFileByPdfJs(documentFile.path)
            }}
          >
            {t('staff.personal.space.contracts.view')}
          </Typography.Link>
        )
      }
    }
  ]

  const missionToStaffColumns: ExpandedColumn<ContractLight, MissionToStaffCompact>[] = [
    {
      accessorKey: 'mission-dates',
      header: t('staff.personal.space.mission.date'),
      display: (mts) => <Badge text={DateFormatter.DDMMYYYY(mts.mission.date)} />
    },
    {
      accessorKey: 'mission-title',
      header: t('staff.personal.space.mission.title'),
      display: (mts) => <Typography variant="secondary-body">{mts.mission.title}</Typography>
    },
    {
      accessorKey: 'mission-pay',
      header: t('staff.personal.space.mission.pay'),
      display: (mts) => (
        <Badge
          text={NumberFormatter.formatCurrency(mts.mission.pay ?? 0)}
          variant={'secondaryBlue'}
        />
      )
    },
    {
      accessorKey: 'contractSentAt',
      header: t('staff.personal.space.mission.contract.sent.at'),
      display: (_, contract) => {
        if (!contract || !contract.contractSentAt) return '-'
        return <Badge text={DateFormatter.DDMMYYYY(contract.contractSentAt)} />
      }
    },
    {
      accessorKey: 'contractSignedAt',
      header: t('staff.personal.space.mission.contract.signed.at'),
      display: (_, contract) => {
        if (!contract || !contract.contractSignedAt) return '-'
        return <Badge text={DateFormatter.DDMMYYYY(contract.contractSignedAt)} />
      }
    },
    {
      accessorKey: 'documentFile',
      header: '',
      display: (_, contract) => {
        if (!contract || !contract.documentFile) return '-'
        return (
          <Typography.Link
            variant={'label-medium'}
            onClick={(e) => {
              e.stopPropagation()
              if (contract.documentFile) viewFileByPdfJs(contract.documentFile.path)
            }}
          >
            Voir contrat
          </Typography.Link>
        )
      }
    }
  ]

  const staffId = user?.staff?.id
  if (!staffId) return null

  return (
    <div className="flex flex-col gap-5">
      <UserNavBar
        title={t('staff.personal.space.contracts')}
        icon={(props) => <FileContractIcon {...props} color={tailwindColors.fushia.DEFAULT} />}
      />

      <Box className="px-5 py-6">
        <Typography variant="h6">{t('staff.personal.space.contracts')}</Typography>

        <DynamicDataTable<ContractLight, ContractFilters, MissionToStaffCompact>
          onLoadingChange={setIsTableLoading}
          version={'LIGHT'}
          className="p-0 mt-4"
          expandedOptions={{ key: 'missionToStaffs', columns: missionToStaffColumns }}
          columns={columns}
          headerType={'minimal'}
          allowSearchParam
          endpoint={constructApiUrl(API.staffs.getStaffContracts.url, { id: staffId })}
          permissions={{ delete: false }}
          filters={selectedFilter}
          filtersLength={countFilters(selectedFilter)}
          onFiltersClick={() => toggleModal('filters')}
          paginated={false}
        />
      </Box>

      {/* Filters Modal */}
      <FilterDrawer<ContractFilters>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <ContractFilter staffId={staffId} filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
